import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  FileEdit,
  XCircle,
  Clock,
  PartyPopper,
  Megaphone,
  CheckCheck,
} from 'lucide-react';
import { useNotifications, useMarkAllNotificationsRead } from '@/lib/queries';

const ICONS: Record<string, any> = {
  LEAVE_APPROVED: { icon: CheckCircle2, color: 'bg-accent-light text-accent' },
  LEAVE_SUBMITTED: { icon: FileEdit, color: 'bg-primary-subtle text-primary' },
  LEAVE_REJECTED: { icon: XCircle, color: 'bg-red-50 text-error' },
  LEAVE_REMINDER: { icon: Clock, color: 'bg-yellow-50 text-warning' },
  BIRTHDAY: { icon: PartyPopper, color: 'bg-purple-50 text-purple-600' },
  ANNOUNCEMENT: { icon: Megaphone, color: 'bg-blue-50 text-info' },
  DEFAULT: { icon: Megaphone, color: 'bg-gray-100 text-gray-600' },
};

function timeAgo(date: string) {
  const d = new Date(date);
  const now = Date.now();
  const diff = Math.floor((now - d.getTime()) / 1000);
  if (diff < 60) return 'Hace unos segundos';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  return `Hace ${Math.floor(diff / 86400)} día(s)`;
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { data } = useNotifications();
  const markAll = useMarkAllNotificationsRead();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-font-primary">Notificaciones</h1>
        <p className="mt-1 text-sm text-font-secondary">
          Centro de notificaciones de tu cuenta
        </p>
      </div>

      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-border p-4">
          <p className="text-sm text-font-secondary">
            {data?.unreadCount ?? 0} sin leer
          </p>
          <button
            onClick={() => markAll.mutate()}
            className="flex h-8 items-center gap-2 rounded-md border border-border bg-white px-3 text-xs font-semibold text-primary hover:bg-primary-subtle"
          >
            <CheckCheck size={14} /> Marcar todas como leídas
          </button>
        </div>

        <div className="divide-y divide-border-light">
          {data?.items?.map((n: any) => {
            const cfg = ICONS[n.type] ?? ICONS.DEFAULT;
            const Icon = cfg.icon;
            return (
              <button
                key={n.id}
                onClick={() => n.link && navigate(n.link)}
                className={`flex w-full items-start gap-4 p-5 text-left transition hover:bg-bg ${
                  !n.readAt ? 'bg-primary-subtle/20' : ''
                }`}
              >
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ${cfg.color}`}
                >
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-font-primary">{n.title}</p>
                    {!n.readAt && (
                      <span className="h-2 w-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-font-secondary">{n.body}</p>
                  <p className="mt-2 text-xs text-font-tertiary">
                    {timeAgo(n.createdAt)}
                  </p>
                </div>
              </button>
            );
          })}
          {(!data?.items || data.items.length === 0) && (
            <p className="py-12 text-center text-sm text-font-secondary">
              No tienes notificaciones
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
